// Google Analytics utility functions
export const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;

// Check InMobi consent for analytics
const checkAnalyticsConsent = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  // If InMobi isn't loaded yet, allow analytics (fallback for development)
  if (!window.__tcfapi) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    // Add timeout to prevent hanging
    const timeout = setTimeout(() => {
      resolve(true);
    }, 3000);

    window.__tcfapi('getTCData', 2, (tcData, success) => {
      clearTimeout(timeout);
      
      if (success && tcData) {
        // Check if this is a GDPR-regulated region
        // If purpose consents are undefined, it means not applicable (non-GDPR)
        const purpose1 = tcData.purpose?.consents?.[1];
        const purpose7 = tcData.purpose?.consents?.[7];
        const isGDPRRegion = purpose1 !== undefined && purpose7 !== undefined;
        
        if (isGDPRRegion) {
          // GDPR region: require explicit consent
          if (purpose1 && purpose7) {
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          // Non-GDPR region (US, etc.): allow tracking by default
          resolve(true);
        }
      } else {
        resolve(true);
      }
    });
  });
};

// Initialize Google Analytics
export const initGA = async () => {
  const hasConsent = await checkAnalyticsConsent();
  if (hasConsent && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page views
export const trackPageView = async (url) => {
  const hasConsent = await checkAnalyticsConsent();
  if (hasConsent && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// Track custom events
export const trackEvent = async (action, category, label, value) => {
  const hasConsent = await checkAnalyticsConsent();
  if (hasConsent && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track user sign up
export const trackSignUp = async (method) => {
  await trackEvent('sign_up', 'engagement', method);
};

// Track word generation
export const trackWordGeneration = async (wordCount, language) => {
  await trackEvent('generate_words', 'ai_usage', language, wordCount);
};

// Track practice usage
export const trackPracticeUsage = async (practiceType, language) => {
  await trackEvent('practice_usage', 'ai_usage', `${practiceType}_${language}`);
};

// Track premium upgrade
export const trackUpgrade = async (plan) => {
  await trackEvent('purchase', 'ecommerce', plan);
};

// Track module creation
export const trackModuleCreation = async (language) => {
  await trackEvent('create_module', 'engagement', language);
};

// Track flashcard usage
export const trackFlashcardUsage = async (language) => {
  await trackEvent('flashcard_usage', 'engagement', language);
};
